function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * Derives the filters a user can add to a cabinet view.
 *
 * A cabinet's `filterDefinitions` are its fixed, always-on filters. Every other
 * eligible index defined for the account is a candidate, provided the content it
 * points at exists in one of the cabinet's form types and the index is in scope for
 * this cabinet (referenced here, or not referenced by any other cabinet).
 * `indexDefinitions` is account-wide with no cabinet provenance, so presence is
 * derived from each form type's `formatInput` and ownership from each cabinet's
 * filter/search/sort/column wiring.
 *
 * Content exists when a field renders it or a behavior writes it. Both tests are
 * needed: `currentState` and `creator` are behavior-written and have no field,
 * while an index like `$.general.reportNumber` names a path no form holds and must
 * not be offered — filtering on it can only ever return nothing.
 *
 * Date and DateTime fields are also excluded: the filter rail only supports discrete
 * options, and those data types do not have working filter behavior today.
 */

import { keyPathsToCriteria } from '../../behaviors/src/lib/formatPaths';
import config from '../config.json';
import { humanizeKey } from './cabinetColumnUtils';

/**
 * From `customCabinetFiltersEnabled` in config (see config/client.js / config/env/*.json).
 * Off globally while the feature is reworked for large datasets (CU-86e2tvrfr). With no
 * candidate filters the rail falls back to the cabinet's own filters and Customize is not
 * rendered. Enable locally via config/env/dev-local.json or TRUQC_CUSTOM_CABINET_FILTERS_ENABLED.
 */
export const CUSTOM_CABINET_FILTERS_ENABLED = Boolean(config.customCabinetFiltersEnabled);
const WILDCARD_SEGMENT = '*';
const BEHAVIOR_TARGET_KEYS = ['targetContentPath', 'newContentPath'];
const REFERENCE_VALUE_TITLE = 'title';

// Discrete filter options (MEMBER OF / LIKE) are not supported for date values, so
// indexes backed by these fields must not appear as customizable filters (CU-86e14uf82).
const UNSUPPORTED_FILTER_COMPONENT_TYPES = new Set(['datePickerField', 'dateTimePickerField', 'weekEndingField']);
const isUnsupportedFilterComponent = component => UNSUPPORTED_FILTER_COMPONENT_TYPES.has(component === null || component === void 0 ? void 0 : component.type);

/**
 * A label only names a filter when it is literal text, or when a behavior exposes a
 * static `defaultValue` string (common on `localizeBehavior`). Other behaviors
 * (`conditionalBehavior`, `getCacheValueBehavior`, ...) resolve against form content
 * the filter rail has no access to, so those fall through and the index is humanized.
 */
const literalLabel = label => {
  if (typeof label === 'string' && label.trim()) {
    return label;
  }
  if (label && typeof label === 'object') {
    const {
      defaultValue
    } = label;
    if (typeof defaultValue === 'string' && defaultValue.trim()) {
      return defaultValue;
    }
  }
  return undefined;
};

/**
 * Segments of an index definition path, e.g. `$.facility.*.facilityID`.
 */
const jsonPathSegments = jsonPath => jsonPath.replace(/^\$\.?/, '').split('.').filter(Boolean);

/**
 * Segments of a formatInput content path, where collections add a wildcard segment.
 */
const contentPathSegments = contentPath => contentPath.split('/').filter(Boolean);
const toDottedPath = segments => segments.join('.');

/**
 * A path and each of its ancestors, deepest first. An index often points below a
 * component, e.g. the `_formRef` of a collection, so walking up finds the field
 * that owns the content.
 */
const pathWithAncestors = segments => segments.map((segment, i) => toDottedPath(segments.slice(0, segments.length - i))).filter(path => path && path !== WILDCARD_SEGMENT);

/**
 * Adds every content path a component's behaviors write to `paths`. A target that
 * starts with a slash is absolute, otherwise it is relative to the component.
 * Behaviors nest sources inside sources, so the whole component is scanned bar its
 * children, which are visited on their own.
 */
const collectBehaviorTargets = (value, componentPath, paths) => {
  if (!value || typeof value !== 'object') {
    return;
  }
  if (Array.isArray(value)) {
    value.forEach(entry => collectBehaviorTargets(entry, componentPath, paths));
    return;
  }
  Object.entries(value).forEach(([key, entry]) => {
    if (key === 'children') {
      return;
    }
    if (BEHAVIOR_TARGET_KEYS.includes(key) && typeof entry === 'string') {
      const absolute = entry.startsWith('/') ? entry : `${componentPath}/${entry}`;
      const dotted = toDottedPath(contentPathSegments(absolute));
      if (dotted) {
        paths.add(dotted);
      }
    }
    collectBehaviorTargets(entry, componentPath, paths);
  });
};

/**
 * What a form type can be filtered on: the component rendering each content path,
 * and the paths its behaviors write.
 *
 * Both are needed. Plenty of filterable content has no component at all —
 * `currentState`, `creator` and `title` are written by behaviors — so components
 * alone cannot tell whether content exists.
 */
const analyzeFormType = formatInput => {
  const componentPaths = {};
  const behaviorPaths = new Set();
  let parsed = formatInput;
  if (typeof formatInput === 'string') {
    try {
      parsed = JSON.parse(formatInput);
    } catch (_unused) {
      return {
        componentPaths,
        behaviorPaths
      };
    }
  }
  let matches = [];
  try {
    matches = keyPathsToCriteria({}, parsed);
  } catch (_unused2) {
    return {
      componentPaths,
      behaviorPaths
    };
  }
  matches.forEach(({
    path: _path = '',
    component
  }) => {
    const dotted = toDottedPath(contentPathSegments(_path));
    if (dotted && !componentPaths[dotted]) {
      componentPaths[dotted] = component;
    }
    collectBehaviorTargets(component, _path, behaviorPaths);
  });
  return {
    componentPaths,
    behaviorPaths
  };
};

/**
 * Indexes another index points at as its reference base (the `_formRef` list). Those
 * are plumbing for the reference filter, never filters of their own.
 *
 * `valueIndex` is deliberately not excluded: it names a real index (often `title`)
 * that must remain a candidate wherever its content exists on the cabinet's forms.
 */
const getReferenceTargets = indexDefinitions => {
  const targets = new Set();
  Object.values(indexDefinitions).forEach(({
    referenceIndex
  }) => {
    if (referenceIndex) targets.add(referenceIndex);
  });
  return targets;
};

/**
 * Index keys a cabinet already wires into filters, search, sort, or columns. Used to
 * tell "this cabinet's indexes" apart from account-wide keys that only other cabinets
 * opted into — `indexDefinitions` itself has no cabinet provenance after merge.
 */
const collectCabinetIndexKeys = (value, keys = new Set()) => {
  if (!value || typeof value !== 'object') {
    return keys;
  }
  if (Array.isArray(value)) {
    value.forEach(entry => collectCabinetIndexKeys(entry, keys));
    return keys;
  }
  Object.entries(value).forEach(([key, entry]) => {
    if ((key === 'index' || key === 'sortIndex') && typeof entry === 'string') {
      keys.add(entry);
    } else if (key === 'searchIndexes' && Array.isArray(entry)) {
      entry.forEach(index => {
        if (typeof index === 'string') keys.add(index);
      });
    } else {
      collectCabinetIndexKeys(entry, keys);
    }
  });
  return keys;
};

/**
 * Whether an account-wide index may be offered on this cabinet. An index the cabinet
 * already references (search/sort/column/filter) is in scope. An index no cabinet
 * references is also in scope — that is how a newly added indexDefinitions entry
 * becomes a customizable filter. Indexes only referenced by *other* cabinets are
 * out of scope, so e.g. Assets' `status` filter does not appear on Jobs.
 */
const isIndexInCabinetScope = (index, cabinetIdentifier, cabinetDefinitions = []) => {
  if (!cabinetIdentifier || !cabinetDefinitions.length) {
    return true;
  }
  let referencedByThis = false;
  let referencedByOther = false;
  cabinetDefinitions.forEach(cabinet => {
    const keys = collectCabinetIndexKeys(cabinet);
    if (!keys.has(index)) {
      return;
    }
    if (cabinet.identifier === cabinetIdentifier) {
      referencedByThis = true;
    } else {
      referencedByOther = true;
    }
  });
  return referencedByThis || !referencedByOther;
};

/**
 * The content path an index reads, following a reference to its base index.
 */
const resolveIndexPath = (index, indexDefinitions) => {
  var _indexDefinitions$ref;
  const {
    path,
    referenceIndex
  } = indexDefinitions[index] || {};
  if (path) {
    return path;
  }
  return referenceIndex ? (_indexDefinitions$ref = indexDefinitions[referenceIndex]) === null || _indexDefinitions$ref === void 0 ? void 0 : _indexDefinitions$ref.path : undefined;
};
const NOT_FOUND = {
  found: false
};
const isContainer = component => Array.isArray(component.children) && component.children.length > 0;

/**
 * Looks for an index's content in one form type, returning the field that names it
 * when there is one.
 *
 * An exact component path is the common case. Indexes often point below a component
 * instead — at the `_formRef` of a search field or the `title` of a picker — so an
 * ancestor match on a field counts too. An ancestor match on a *container* proves
 * nothing: a section's path is only a namespace, so `$.general.reportNumber` would
 * "match" the General section of a form that has no report number at all. Content
 * under a container is only real when a behavior writes it.
 */
const matchIndexToFormType = (indexPath, {
  componentPaths,
  behaviorPaths
}) => {
  const candidatePaths = pathWithAncestors(jsonPathSegments(indexPath));
  for (let i = 0; i < candidatePaths.length; i += 1) {
    const path = candidatePaths[i];
    const component = componentPaths[path];

    // Date / DateTime fields cannot be offered as customizable filters.
    if (component && isUnsupportedFilterComponent(component)) {
      return NOT_FOUND;
    }
    if (component && (i === 0 || !isContainer(component))) {
      return {
        found: true,
        label: literalLabel(component.label)
      };
    }
    if (behaviorPaths.has(path)) {
      return {
        found: true
      };
    }
    if (component) {
      const written = candidatePaths.slice(0, i).some(deeper => behaviorPaths.has(deeper));
      return written ? {
        found: true
      } : NOT_FOUND;
    }
  }
  return NOT_FOUND;
};

/**
 * An index only has to be real in one of the cabinet's form types. A match that
 * names the filter wins over one that only proves the content exists.
 */
const preferredMatch = (left, right) => {
  if (left.label) {
    return left;
  }
  if (right.label) {
    return right;
  }
  return left.found ? left : right;
};
const matchIndex = (indexPath, formTypeMaps) => formTypeMaps.map(formTypeMap => matchIndexToFormType(indexPath, formTypeMap)).reduce(preferredMatch, NOT_FOUND);

/**
 * Names a filter. A field's own label is the best name; content written by a
 * behavior has no field to borrow from, so the index key is humanized instead.
 * A reference index reads one value off a referenced form, and every reference
 * onto the same form matches that form's field: `projectTitle` and `afeNumberTitle`
 * both match the Project search field. The value being read keeps them apart.
 */
const buildTitle = (index, indexDefinitions, matchLabel) => {
  const base = matchLabel || humanizeKey(index);
  const {
    referenceIndex,
    valueIndex
  } = indexDefinitions[index];
  if (matchLabel && referenceIndex && valueIndex && valueIndex !== REFERENCE_VALUE_TITLE) {
    return `${base} ${humanizeKey(valueIndex)}`;
  }
  return base;
};

/**
 * Guarantees no two filters read the same in the list, since a shared title makes
 * them impossible to tell apart. Index keys are unique, so humanizing them is
 * always enough to break a tie.
 */
const withUniqueTitles = candidates => {
  const counts = {};
  candidates.forEach(({
    title
  }) => {
    counts[title] = (counts[title] || 0) + 1;
  });
  return candidates.map(candidate => counts[candidate.title] > 1 ? _objectSpread(_objectSpread({}, candidate), {}, {
    title: humanizeKey(candidate.index)
  }) : candidate);
};

/**
 * Filters a user can add to this cabinet, ordered by title.
 *
 * @returns {Array<{ index: string, title: string }>}
 */
export const getCabinetCandidateFilters = (indexDefinitions = {}, filterDefinitions = [], formTypeIdentifiers = [], formTypesByIdentifier = {}, cabinetDefinitions = [], cabinetIdentifier = undefined) => {
  const mappedIndexes = new Set(filterDefinitions.map(({
    index
  }) => index));
  const referenceTargets = getReferenceTargets(indexDefinitions);
  const unmapped = Object.keys(indexDefinitions).filter(index => !mappedIndexes.has(index) && !referenceTargets.has(index)
  // `single` indexes are scalar join keys for BI that duplicate a sibling index.
  && !indexDefinitions[index].single && isIndexInCabinetScope(index, cabinetIdentifier, cabinetDefinitions)).map(index => ({
    index,
    indexPath: resolveIndexPath(index, indexDefinitions)
  })).filter(({
    indexPath
  }) => indexPath);

  // reading the form types is the costly part, so it waits until there is
  // something a cabinet could actually be offered
  if (!unmapped.length) {
    return [];
  }
  const formTypeMaps = formTypeIdentifiers.map(identifier => {
    var _formTypesByIdentifie;
    return (_formTypesByIdentifie = formTypesByIdentifier[identifier]) === null || _formTypesByIdentifie === void 0 ? void 0 : _formTypesByIdentifie.formatInput;
  }).filter(Boolean).map(analyzeFormType);
  if (!formTypeMaps.length) {
    return [];
  }
  const candidates = unmapped.map(({
    index,
    indexPath
  }) => {
    const {
      found,
      label
    } = matchIndex(indexPath, formTypeMaps);
    return found ? {
      index,
      title: buildTitle(index, indexDefinitions, label)
    } : null;
  }).filter(Boolean);
  return withUniqueTitles(candidates).sort((a, b) => a.title.localeCompare(b.title));
};

/**
 * Drops persisted filters that the cabinet no longer offers and duplicates, keeping
 * the user's chosen order.
 */
export const normalizeAddedFilterIndexes = (savedIndexes = [], candidateFilters = []) => {
  const available = new Set(candidateFilters.map(({
    index
  }) => index));
  const seen = new Set();
  return savedIndexes.filter(index => {
    if (!available.has(index) || seen.has(index)) {
      return false;
    }
    seen.add(index);
    return true;
  });
};

/**
 * Drops filters this cabinet cannot offer options for, which the server tells us by refusing
 * the options request for an index it has no definition of. That answer cannot change while
 * the user is here, so the filter is left out of the rail instead of failing again.
 *
 * An empty option list is not grounds for removal: it only says nothing matches right now.
 * Neither is any other request failure, which may be nothing worse than a bad connection.
 * A filter the user already selected stays as well, so they can still clear it.
 */
export const removeUnavailableFilters = (filterDefinitions = [], cabinetFilterOptions = {}, selections = {}) => filterDefinitions.filter(({
  index
}) => {
  var _cabinetFilterOptions;
  const selection = selections[index];
  if (Array.isArray(selection) && selection.length > 0) {
    return true;
  }
  return !((_cabinetFilterOptions = cabinetFilterOptions[index]) !== null && _cabinetFilterOptions !== void 0 && _cabinetFilterOptions.undefinedOnServer);
});

/**
 * The filter definitions the panel renders: the cabinet's own filters first, then
 * the ones the user added, in the order they were added.
 */
export const buildEffectiveFilterDefinitions = (filterDefinitions = [], addedFilterIndexes = [], candidateFilters = []) => {
  const added = addedFilterIndexes.map(index => {
    const candidate = candidateFilters.find(filter => filter.index === index);
    return {
      index,
      title: (candidate === null || candidate === void 0 ? void 0 : candidate.title) || humanizeKey(index)
    };
  });
  return [...filterDefinitions, ...added];
};